import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { AccessTokenDetails, Branch, extractQueryParams, SearchCriteria } from '@raptor-fe/core';
import { WebSelectBranchModalComponent } from './web-select-branch-modal.component';
import {
    ApplicationContextStore,
    AuthService,
    BranchService,
    NamedRoutes,
    RouterService,
    ToastService,
    TokenService,
    URLService,
    UserService,
} from '@raptor-fe/services';
import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, pipe, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';

export interface SelectBranchModalStore {
    selectedBranch: Branch | null;
    branches: Branch[];
    searchCriteria: SearchCriteria;
    loading: boolean;
    redirectRoute: string | null;
    isScrollDisabled: boolean;
}

export const WebSelectBranchModalStore = signalStore(
    { providedIn: 'root' }, // service is provided in root to avoid multiple instances
    withState<SelectBranchModalStore>({
        selectedBranch: null,
        branches: [],
        searchCriteria: {
            pageNumber: 1,
            pageSize: 20,
            items: [],
            orderBy: 'name',
        },
        loading: false,
        redirectRoute: null,
        isScrollDisabled: false,
    }),
    withMethods(
        (
            store,
            dialogService = inject(DialogService),
            router = inject(Router),
            routerService = inject(RouterService),
            service = inject(BranchService),
            toastService = inject(ToastService),
            userService = inject(UserService),
            applicationContextStore = inject(ApplicationContextStore),
            tokenService = inject(TokenService),
            authService = inject(AuthService),
            urlService = inject(URLService),
        ) => ({
            // open dialog
            open(redirectRoute?: string | null): void {
                dialogService.open({
                    content: WebSelectBranchModalComponent,
                });

                patchState(store, {
                    redirectRoute: redirectRoute,
                });
            },
            // close dialog
            async close(dialogRef: DialogRef): Promise<void> {
                if (store.selectedBranch()) {
                    dialogRef?.close();

                    //Set the new current branch for the user
                    await firstValueFrom(
                        userService.setUserBranchId(applicationContextStore.tenantId(), store.selectedBranch()?.id ?? '').pipe(
                            catchError(async (error) => {
                                await toastService.showError(error?.message ?? 'Error changing user branch. Please try again.');
                            }),
                        ),
                    );

                    await firstValueFrom(
                        authService.refreshToken(tokenService.getRefreshToken() ?? '').pipe(
                            switchMap(async (res) => {
                                if ((res as AccessTokenDetails)?.access_token) {
                                    const openToken = authService.getDecodedAccessToken((res as AccessTokenDetails).access_token as string);
                                    applicationContextStore.setRoles(openToken?.role ?? []);
                                    applicationContextStore.setBranch(store.selectedBranch() as Branch);
                                    if (store.selectedBranch()?.serverUrl) {
                                        urlService.saveURL(store.selectedBranch()?.serverUrl as string);
                                    }
                                }
                            }),
                            catchError(async (error) => {
                                await toastService.showError(error?.message ?? 'Error refreshing token. Please try again.');
                            }),
                        ),
                    );

                    if (store.redirectRoute()) {
                        const { routePath, queryParams } = extractQueryParams(store.redirectRoute() as string);
                        router.navigate([routePath], { queryParams });
                    } else {
                        routerService.navigateAndReplaceUrl(NamedRoutes.landing());
                    }
                } else {
                    await toastService.displayToast('Please select a branch before close it', 'warning');
                }
            },
            // fetch branches
            fetchBranches: rxMethod<SearchCriteria>(
                pipe(
                    debounceTime(300),
                    distinctUntilChanged(
                        (prev, curr) =>
                            prev.items[0]?.value === curr.items[0]?.value && prev.pageNumber === curr.pageNumber && prev.pageSize === curr.pageSize,
                    ),
                    tap(() => patchState(store, { loading: true })),
                    switchMap((searchCriteria) =>
                        service.search(applicationContextStore.tenantId(), searchCriteria).pipe(
                            tapResponse({
                                next: (branches) => {
                                    if (branches.length) {
                                        if ((store.searchCriteria().pageNumber ?? 1) > 1) {
                                            patchState(store, {
                                                branches: [...store.branches(), ...branches],
                                            });
                                        } else {
                                            patchState(store, { branches });
                                        }
                                    } else patchState(store, { isScrollDisabled: true });

                                    const selectedBranch = branches.find((b) => b.id === applicationContextStore.branch()?.id);
                                    if (selectedBranch) {
                                        patchState(store, { selectedBranch: selectedBranch });
                                    }
                                },
                                error: async () => {
                                    await toastService.showError('Error fetching branches');
                                    patchState(store, {
                                        loading: false,
                                    });
                                },
                                finalize: () => patchState(store, { loading: false }),
                            }),
                        ),
                    ),
                ),
            ),
            // reset page number
            resetPageNumber(): void {
                patchState(store, {
                    searchCriteria: { ...store.searchCriteria(), pageNumber: 1 },
                    isScrollDisabled: false,
                });
            },
            // increase page number
            increasePageNumber(): void {
                patchState(store, {
                    searchCriteria: {
                        ...store.searchCriteria(),
                        pageNumber: (store.searchCriteria().pageNumber ?? 1) + 1,
                    },
                });
            },
            // search branches using keyword
            updateSearchTerm(keyword: string): void {
                patchState(store, {
                    searchCriteria: {
                        ...store.searchCriteria(),
                        pageNumber: 1,
                        items: [
                            {
                                name: 'name',
                                value: keyword,
                                condition: 'and',
                                operator: 'like',
                            },
                        ],
                    },
                    isScrollDisabled: false,
                });
            },
            // set selected branch
            setSelectedBranch(branch: Branch): void {
                patchState(store, { selectedBranch: branch });
            },
        }),
    ),
    withHooks({
        onInit(store, applicationContextStore = inject(ApplicationContextStore)) {
            const selectedBranch = applicationContextStore.branch();
            patchState(store, { selectedBranch: selectedBranch?.id ? selectedBranch : null });
            store.fetchBranches(store.searchCriteria);
        },
    }),
);
